import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppRouter } from './app/router';
import { I18nProvider } from './i18n';
import { SessionProvider } from './lib/session';
import { ApiError } from './lib/api';
import { ErrorBoundary } from './app/ErrorBoundary';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Clinical and operational data goes stale fast; a waiting-room row that
      // is 30 seconds old is misleading. Screens that need faster set their own.
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Retrying a 401/403/402/409 cannot succeed and only delays the user
        // seeing the state that explains the failure. Rate limits are worse:
        // retrying is what caused them.
        if (error instanceof ApiError) {
          if (
            error.isUnauthenticated ||
            error.isForbidden ||
            error.isSubscriptionBlocked ||
            error.isConflict ||
            error.isValidation ||
            error.isRateLimited
          ) {
            return false;
          }
        }
        return failureCount < 2;
      },
    },
    mutations: {
      // Never automatic. A retried checkout or payment without an idempotency
      // key takes the money twice; the call sites that are safe to retry pass
      // the key and handle it themselves.
      retry: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');

createRoot(container).render(
  <StrictMode>
    <ErrorBoundary>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <AppRouter />
          </SessionProvider>
        </QueryClientProvider>
      </I18nProvider>
    </ErrorBoundary>
  </StrictMode>,
);
